/**
 * Linear Scale & Domain Utilities (T-05)
 * 
 * Core coordinate system for number line transformations between:
 * - World coordinates: Real number values on the number line
 * - Screen coordinates: Pixel positions in the viewport
 * 
 * Key concepts:
 * - Scale factor: Pixels per unit (changes with zoom)
 * - Offset: Translation for panning
 * - Domain: Visible range of values
 * 
 * This module provides two domain calculation strategies:
 * - computeVisibleDomain(): Left-edge positioning (matches pan offset directly)
 * - computeCenteredDomain(): Center-based positioning (for centering on specific values)
 */

/**
 * Represents a numeric domain/range with start and end values
 */
export interface Domain {
    start: number;
    end: number;
  }
  
  /**
   * Represents zoom constraints
   */
  export interface ZoomBounds {
    min: number;
    max: number;
  }
  
  /**
   * Default zoom bounds to prevent excessive zoom in/out
   */
  export const DEFAULT_ZOOM_BOUNDS: ZoomBounds = {
    min: 0.001,  // Very zoomed out (1 pixel = 1000 units)
    max: 1000,   // Very zoomed in (1000 pixels = 1 unit)
  };
  
  /**
   * Convert a world coordinate value to screen pixel position
   * 
   * @param value - The world coordinate value
   * @param domain - The visible domain (start/end values)
   * @param width - The screen width in pixels
   * @returns Pixel position relative to the left edge
   * 
   * @example
   * // Domain shows values 0-10, screen is 800px wide
   * // Value 5 should be at pixel 400 (middle)
   * valueToPx(5, { start: 0, end: 10 }, 800) // Returns 400
   */
  export function valueToPx(value: number, domain: Domain, width: number): number {
    if (domain.end === domain.start) {
      return width / 2; // Center for zero-width domain
    }
    
    const domainWidth = domain.end - domain.start;
    const normalizedPosition = (value - domain.start) / domainWidth;
    return normalizedPosition * width;
  }
  
  /**
   * Convert a screen pixel position to world coordinate value
   * 
   * @param px - The pixel position relative to the left edge
   * @param domain - The visible domain (start/end values)
   * @param width - The screen width in pixels
   * @returns World coordinate value
   * 
   * @example
   * // Domain shows values 0-10, screen is 800px wide
   * // Pixel 400 should be value 5 (middle)
   * pxToValue(400, { start: 0, end: 10 }, 800) // Returns 5
   */
  export function pxToValue(px: number, domain: Domain, width: number): number {
    if (width === 0) {
      return domain.start; // Default for zero-width screen
    }
    
    const domainWidth = domain.end - domain.start;
    const normalizedPosition = px / width;
    return domain.start + normalizedPosition * domainWidth;
  }
  
  /**
   * Compute the visible domain based on viewport state
   * 
   * This function calculates what range of values is currently visible
   * based on the pan offset, zoom scale, and screen width.
   * 
   * The domain starts at the left edge position and extends rightward
   * by the visible range. Use computeCenteredDomain() if you want
   * the view centered on a specific value instead.
   * 
   * @param offsetX - Pan offset in pixels (positive = panned right, shows more negative values)
   * @param scale - Zoom scale in pixels per unit
   * @param width - Screen width in pixels
   * @returns Domain representing the visible range
   * 
   * @example
   * // No pan, scale of 100px per unit, 800px wide screen
   * // Shows 8 units starting from 0: [0, 8]
   * computeVisibleDomain(0, 100, 800) // Returns { start: 0, end: 8 }
   * 
   * @example
   * // Pan right 200px, same scale and width
   * // Shows values shifted left: [-2, 6]
   * computeVisibleDomain(200, 100, 800) // Returns { start: -2, end: 6 }
   */
  export function computeVisibleDomain(offsetX: number, scale: number, width: number): Domain {
    if (scale <= 0) {
      throw new Error('Scale must be positive');
    }
    
    // Calculate the range of values visible on screen
    const visibleRange = width / scale;
    
    // Convert offset to world coordinates
    // Positive offsetX means we've panned right, so left edge is more negative
    let leftEdgeValue = -offsetX / scale;
    
    // Handle negative zero case (JavaScript quirk: -0 !== 0 in Object.is)
    if (leftEdgeValue === 0) {
      leftEdgeValue = 0;
    }
    
    return {
      start: leftEdgeValue,
      end: leftEdgeValue + visibleRange,
    };
  }
  
  /**
   * Clamp zoom scale within acceptable bounds
   * 
   * @param scale - The proposed scale value
   * @param bounds - Min/max scale limits (optional, uses defaults)
   * @returns Clamped scale value
   * 
   * @example
   * clampZoom(0.0001, { min: 0.001, max: 1000 }) // Returns 0.001
   * clampZoom(5000, { min: 0.001, max: 1000 }) // Returns 1000
   * clampZoom(50) // Returns 50 (within default bounds)
   */
  export function clampZoom(scale: number, bounds: ZoomBounds = DEFAULT_ZOOM_BOUNDS): number {
    return Math.max(bounds.min, Math.min(bounds.max, scale));
  }
  
  /**
   * Calculate the scale needed to fit a specific domain within the screen width
   * 
   * @param domain - The domain to fit
   * @param width - Screen width in pixels
   * @returns Scale factor (pixels per unit)
   * 
   * @example
   * // To fit values 0-10 in 800px width
   * scaleToFitDomain({ start: 0, end: 10 }, 800) // Returns 80
   */
  export function scaleToFitDomain(domain: Domain, width: number): number {
    const domainWidth = Math.abs(domain.end - domain.start);
    if (domainWidth === 0) {
      return DEFAULT_ZOOM_BOUNDS.max; // Very zoomed in for zero-width domain
    }
    
    return width / domainWidth;
  }
  
  /**
   * Calculate the offset needed to center a specific value on screen
   * 
   * @param centerValue - The value to center
   * @param scale - Current scale (pixels per unit)
   * @param width - Screen width in pixels
   * @returns Offset in pixels
   * 
   * @example
   * // Center value 5 on 800px wide screen with scale 100
   * offsetToCenterValue(5, 100, 800) // Returns -100
   */
  export function offsetToCenterValue(centerValue: number, scale: number, width: number): number {
    // To center a value, we need the offset such that the value appears at width/2
    // valueToPx formula: px = (value - domain.start) * scale + offsetX
    // For center: width/2 = (centerValue - domain.start) * scale + offsetX
    // With domain.start = -offsetX/scale, we get:
    // width/2 = (centerValue + offsetX/scale) * scale + offsetX
    // width/2 = centerValue * scale + 2 * offsetX
    // offsetX = (width/2 - centerValue * scale) / 2
    
    return (width / 2) - (centerValue * scale);
  }
  
  /**
   * Compute a domain centered on a specific value
   * 
   * This is different from computeVisibleDomain which positions the left edge.
   * This function centers the view on a specific value.
   * 
   * @param centerValue - The value to center the view on
   * @param scale - Zoom scale in pixels per unit
   * @param width - Screen width in pixels
   * @returns Domain centered on the specified value
   * 
   * @example
   * // Center view on value 5 with scale 100px/unit on 800px screen
   * computeCenteredDomain(5, 100, 800) // Returns { start: 1, end: 9 }
   */
  export function computeCenteredDomain(centerValue: number, scale: number, width: number): Domain {
    if (scale <= 0) {
      throw new Error('Scale must be positive');
    }
    
    const visibleRange = width / scale;
    const halfRange = visibleRange / 2;
    
    return {
      start: centerValue - halfRange,
      end: centerValue + halfRange,
    };
  }
   /** 
   * @param value - The value to check
   * @param domain - Current visible domain
   * @returns True if the value is visible
   */
  /**
   * Check if a value is visible within the current domain
   * 
   * @param value - The value to check
   * @param domain - Current visible domain
   * @returns True if the value is visible
   */
  export function isValueVisible(value: number, domain: Domain): boolean {
    return value >= domain.start && value <= domain.end;
  }
  
  /**
   * Round-trip conversion test utility
   * Tests the accuracy of value→px→value conversion
   * 
   * @param value - Original value
   * @param domain - Domain for conversion
   * @param width - Screen width
   * @returns Object with original value, converted pixel, and round-trip value
   */
  export function testRoundTripAccuracy(value: number, domain: Domain, width: number) {
    const px = valueToPx(value, domain, width);
    const roundTripValue = pxToValue(px, domain, width);
    const error = Math.abs(value - roundTripValue);
    
    return {
      originalValue: value,
      pixelPosition: px,
      roundTripValue,
      error,
      withinTolerance: error < 1e-10, // Very tight tolerance for floating point
    };
  }
  
  /**
   * Get the value at a specific pixel with sub-pixel precision
   * Useful for snapping and precise positioning
   * 
   * @param px - Pixel position (can be fractional)
   * @param domain - Current domain
   * @param width - Screen width
   * @returns Precise value at that pixel
   */
  export function getValueAtPixel(px: number, domain: Domain, width: number): number {
    return pxToValue(px, domain, width);
  }
  
  /**
   * Get the pixel position of a value with sub-pixel precision
   * Useful for smooth animations and precise rendering
   * 
   * @param value - World coordinate value
   * @param domain - Current domain
   * @param width - Screen width
   * @returns Precise pixel position
   */
  export function getPixelAtValue(value: number, domain: Domain, width: number): number {
    return valueToPx(value, domain, width);
  }